import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Check,
    Copy,
    KeyRound,
    MessageCircle,
    ShieldCheck,
    Smartphone,
    UserPlus,
} from 'lucide-react';

import { createClientUser, resetUserPin } from '@/app/services/partyService';
import { whatsappUrl } from '@/app/lib/phone';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import type { Party, PartyUser } from '@/app/types/party';

interface Props {
    party: Party;
    /** The existing client login, when resetting. Null when creating a new one. */
    clientUser: PartyUser | null;
    mode: 'create' | 'reset';
    open: boolean;
    onClose: () => void;
}

interface Credentials {
    username: string;
    pin: string;
}

/** Message the rep sends the customer — plain, no jargon, works in any chat app. */
const shareMessage = (
    partyName: string,
    creds: Credentials,
    mode: 'create' | 'reset'
): string =>
    (mode === 'create'
        ? `Hi ${partyName}, your app login is ready.\n\n`
        : `Hi ${partyName}, here is your new app PIN.\n\n`) +
    `User ID: ${creds.username}\n` +
    `PIN: ${creds.pin}\n\n` +
    `Please keep this PIN safe.`;

export function ClientAccessSheet({ party, clientUser, mode, open, onClose }: Props) {
    const queryClient = useQueryClient();
    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [copied, setCopied] = useState<'username' | 'pin' | null>(null);

    // The PIN lives in component state only, so wipe it whenever the sheet is
    // reopened — a stale PIN on screen is worse than no PIN.
    useEffect(() => {
        if (open) {
            setCredentials(null);
            setErrorMessage(null);
            setCopied(null);
        }
    }, [open]);

    const failed = (err: unknown, fallback: string) => {
        let message = fallback;
        if (axios.isAxiosError(err)) {
            message = err.response?.data?.detail || err.message || fallback;
        } else if (err instanceof Error) {
            message = err.message;
        }
        setErrorMessage(message);
    };

    const succeeded = (creds: Credentials, message: string) => {
        setCredentials(creds);
        setErrorMessage(null);
        // The party's user list changes on create, so the detail page reflects
        // "Reset PIN" straight after without a manual refresh.
        queryClient.invalidateQueries({ queryKey: ['party-users', party.id] });
        toast.success(message);
    };

    const createMutation = useMutation({
        mutationFn: () => createClientUser(party.id),
        onSuccess: (data) =>
            succeeded({ username: data.username, pin: data.pin }, 'App login created'),
        onError: (err) => failed(err, 'Could not create the app login'),
    });

    const resetMutation = useMutation({
        mutationFn: () => {
            if (!clientUser) {
                throw new Error('This party has no app login to reset');
            }
            return resetUserPin(clientUser.id);
        },
        onSuccess: (data) =>
            succeeded({ username: data.username, pin: data.new_pin }, 'PIN reset'),
        onError: (err) => failed(err, 'Could not reset the PIN'),
    });

    const mutation = mode === 'create' ? createMutation : resetMutation;
    const isPending = mutation.isPending;

    const handleCopy = async (field: 'username' | 'pin', value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        } catch {
            toast.error('Copy failed — note it down manually');
        }
    };

    const handleShare = () => {
        if (!credentials || !party.phone) return;
        window.open(
            whatsappUrl(party.phone, shareMessage(party.name, credentials, mode)),
            '_blank'
        );
    };

    const handleClose = () => {
        if (isPending) return;
        onClose();
    };

    const title = credentials
        ? mode === 'create'
            ? 'Login created'
            : 'PIN reset'
        : mode === 'create'
            ? 'Create app login'
            : 'Reset PIN';

    return (
        <MobileBottomSheet
            open={open}
            onClose={handleClose}
            title={title}
            heightMode="auto"
            showCloseButton={!isPending}
        >
            <div className="p-4 space-y-5">
                {!credentials ? (
                    <>
                        <div className="rounded-xl bg-secondary p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <Smartphone className="h-5 w-5 text-primary flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-muted-foreground">
                                        {mode === 'create' ? 'User ID will be' : 'User ID'}
                                    </p>
                                    <p className="font-medium tabular-nums truncate">
                                        {mode === 'create' ? party.phone : clientUser?.username}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <KeyRound className="h-5 w-5 text-primary flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-muted-foreground">PIN</p>
                                    <p className="font-medium">
                                        A new 4-digit PIN, generated now
                                    </p>
                                </div>
                            </div>
                        </div>

                        <p className="text-sm text-muted-foreground">
                            {mode === 'create'
                                ? `${party.name} will be able to sign into the customer app with these. Share them face to face or on WhatsApp.`
                                : `The current PIN for ${clientUser?.username} stops working immediately. Any other logins on this party are unaffected.`}
                        </p>

                        {errorMessage && (
                            <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
                                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-destructive">{errorMessage}</p>
                            </div>
                        )}

                        <div className="space-y-3 pt-1">
                            <MobileButton
                                type="button"
                                size="lg"
                                className="w-full"
                                isLoading={isPending}
                                icon={
                                    mode === 'create' ? (
                                        <UserPlus className="h-5 w-5" />
                                    ) : (
                                        <KeyRound className="h-5 w-5" />
                                    )
                                }
                                onClick={() => mutation.mutate()}
                            >
                                {mode === 'create' ? 'Create login' : 'Generate new PIN'}
                            </MobileButton>
                            <MobileButton
                                type="button"
                                variant="secondary"
                                className="w-full"
                                disabled={isPending}
                                onClick={handleClose}
                            >
                                Cancel
                            </MobileButton>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex items-start gap-3 rounded-xl border border-green-500/30 bg-green-500/10 p-3">
                            <ShieldCheck className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-green-500">
                                {mode === 'create'
                                    ? 'Login created. Share it with the customer now.'
                                    : 'PIN reset. Share the new PIN with the customer now.'}
                            </p>
                        </div>

                        <div className="space-y-3">
                            <CredentialRow
                                label="User ID"
                                value={credentials.username}
                                copied={copied === 'username'}
                                onCopy={() => handleCopy('username', credentials.username)}
                            />
                            <CredentialRow
                                label="PIN"
                                value={credentials.pin}
                                large
                                copied={copied === 'pin'}
                                onCopy={() => handleCopy('pin', credentials.pin)}
                            />
                        </div>

                        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-500">
                                This PIN is shown once. Once you close this, it can only be
                                replaced by resetting again.
                            </p>
                        </div>

                        <div className="space-y-3 pt-1">
                            {party.phone && (
                                <MobileButton
                                    type="button"
                                    size="lg"
                                    className="w-full"
                                    icon={<MessageCircle className="h-5 w-5" />}
                                    onClick={handleShare}
                                >
                                    Send on WhatsApp
                                </MobileButton>
                            )}
                            <MobileButton
                                type="button"
                                variant="secondary"
                                className="w-full"
                                onClick={onClose}
                            >
                                Done
                            </MobileButton>
                        </div>
                    </>
                )}
            </div>
        </MobileBottomSheet>
    );
}

interface CredentialRowProps {
    label: string;
    value: string;
    large?: boolean;
    copied: boolean;
    onCopy: () => void;
}

function CredentialRow({ label, value, large, copied, onCopy }: CredentialRowProps) {
    return (
        <button
            type="button"
            onClick={onCopy}
            className="w-full flex items-center justify-between gap-3 rounded-xl bg-secondary p-4 text-left active:bg-accent"
        >
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p
                    className={
                        large
                            ? 'text-3xl font-bold tabular-nums tracking-[0.3em]'
                            : 'text-lg font-semibold tabular-nums truncate'
                    }
                >
                    {value}
                </p>
            </div>
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-background flex-shrink-0">
                {copied ? (
                    <Check className="h-5 w-5 text-green-500" />
                ) : (
                    <Copy className="h-5 w-5 text-muted-foreground" />
                )}
            </span>
        </button>
    );
}
